import { ICommand } from './ICommand';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { useDiagramStore } from '../../stores/diagramStore';

interface UpdateNodeTextCommandParams {
  nodeId: string;
  field: 'name' | 'location' | string;
  newText: string;
  oldText: string;
  newHeight?: number;
  oldHeight?: number;
  newFlowchartTextLayout?: Record<string, unknown> | null;
  oldFlowchartTextLayout?: Record<string, unknown> | null;
}

export class UpdateNodeTextCommand implements ICommand {
  id = `update-text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  name = 'Update Node Text';
  timestamp = Date.now();
  
  constructor(
    private params: UpdateNodeTextCommandParams
  ) {}
  
  async execute(): Promise<void> {
    const mutationManager = getNodeMutationManager();
    const node = useDiagramStore.getState().nodes.find((n) => n.id === this.params.nodeId) as any;
    const changes: Record<string, any> = {
      [this.params.field]: this.params.newText
    };
    const currentAttrs = (node?.attrs || {}) as Record<string, unknown>;
    changes.attrs = {
      ...currentAttrs,
      [this.params.field]: this.params.newText,
    };

    // Flowchart, BPMN, basic, and org shapes can still rely on "label" in some render/export paths.
    // Keep it aligned with the committed text so double-click text edits remain consistent everywhere.
    try {
      const shouldSyncLabel =
        node?.type === 'flowchart-shape' ||
        node?.type === 'bpmn-shape' ||
        node?.type === 'basic-shape' ||
        node?.type === 'org-shape';
      if (shouldSyncLabel && this.params.field === 'text') {
        changes.label = this.params.newText;
        changes.attrs.label = this.params.newText;
        changes.attrs.text = this.params.newText;
        if (this.params.newFlowchartTextLayout) {
          changes.attrs.flowchartTextLayout = this.params.newFlowchartTextLayout;
        } else if (node?.type === 'flowchart-shape') {
          delete changes.attrs.flowchartTextLayout;
        }
      }
    } catch {}

    if (typeof this.params.newHeight === 'number') {
      // Set both height formats for compatibility
      changes.height = this.params.newHeight;
      changes.h = this.params.newHeight;
      // Note: width is preserved by NodeMutationManager when spreading currentYjsNode
    }

    // 1) Write to Yjs/store via MutationManager (legacy path)
    mutationManager.applyNodeChange(this.params.nodeId, changes);

    // 2) Mirror to Zustand store to mark node as dirty so TanStack/Electric
    //    projection can persist the change. This avoids a race where the
    //    projection overwrites text with the default value.
    try {
      const { updateNode } = useDiagramStore.getState();
      updateNode(this.params.nodeId, changes);
    } catch {}
  }
  
  async undo(): Promise<void> {
    const mutationManager = getNodeMutationManager();
    const node = useDiagramStore.getState().nodes.find((n) => n.id === this.params.nodeId) as any;
    const changes: Record<string, any> = {
      [this.params.field]: this.params.oldText
    };
    const currentAttrs = (node?.attrs || {}) as Record<string, unknown>;
    changes.attrs = {
      ...currentAttrs,
      [this.params.field]: this.params.oldText,
    };

    try {
      const shouldSyncLabel =
        node?.type === 'flowchart-shape' ||
        node?.type === 'bpmn-shape' ||
        node?.type === 'basic-shape' ||
        node?.type === 'org-shape';
      if (shouldSyncLabel && this.params.field === 'text') {
        changes.label = this.params.oldText;
        changes.attrs.label = this.params.oldText;
        changes.attrs.text = this.params.oldText;
        if (this.params.oldFlowchartTextLayout) {
          changes.attrs.flowchartTextLayout = this.params.oldFlowchartTextLayout;
        } else if (node?.type === 'flowchart-shape') {
          delete changes.attrs.flowchartTextLayout;
        }
      }
    } catch {}

    if (this.params.newHeight !== undefined && this.params.oldHeight !== undefined) {
      changes.height = this.params.oldHeight;
      changes.h = this.params.oldHeight;
    }

    mutationManager.applyNodeChange(this.params.nodeId, changes);
    try {
      const { updateNode } = useDiagramStore.getState();
      updateNode(this.params.nodeId, changes);
    } catch {}
  }
  
  async redo(): Promise<void> {
    await this.execute();
  }
  
  canMergeWith(other: ICommand): boolean {
    if (!(other instanceof UpdateNodeTextCommand)) return false;
    
    const otherCommand = other as UpdateNodeTextCommand;
    
    // Merge if it's the same node and field being edited
    return (
      this.params.nodeId === otherCommand.params.nodeId &&
      this.params.field === otherCommand.params.field &&
      // Only merge if edits are happening within a short time window
      Math.abs(this.timestamp - otherCommand.timestamp) < 1000
    );
  }
  
  mergeWith(other: ICommand): ICommand {
    if (!(other instanceof UpdateNodeTextCommand)) return this;
    
    const otherCommand = other as UpdateNodeTextCommand;
    
    // Keep the original oldText but use the new text from the latest command
    return new UpdateNodeTextCommand({
      nodeId: this.params.nodeId,
      field: this.params.field,
      newText: otherCommand.params.newText,
      oldText: this.params.oldText,
      newHeight: otherCommand.params.newHeight ?? this.params.newHeight,
      oldHeight: otherCommand.params.oldHeight ?? otherCommand.params.oldHeight,
      newFlowchartTextLayout: otherCommand.params.newFlowchartTextLayout !== undefined
        ? otherCommand.params.newFlowchartTextLayout
        : this.params.newFlowchartTextLayout,
      oldFlowchartTextLayout: this.params.oldFlowchartTextLayout,
    });
  }
  
  getMetadata(): Record<string, any> {
    const metadata: Record<string, any> = {
      nodeId: this.params.nodeId,
      field: this.params.field,
      textChange: `"${this.params.oldText}" → "${this.params.newText}"`
    };

    if (this.params.newHeight !== undefined) {
      metadata.heightChange = `${this.params.oldHeight ?? 'unchanged'} → ${this.params.newHeight}`;
    }

    return metadata;
  }
}
